"use client";

import React, { useState, useEffect, useRef, useCallback } from "react";
import Image from "next/image";
import Link from "next/link";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface Picture {
  data?: {
    id?: string;
    attributes?: {
      url?: string;
      name?: string;
      alternativeText?: string;
    };
  };
}

interface CardProps {
  image?: Picture;
  title?: string;
  subtitle?: string;
  buttonText?: string;
  mediaUrl?: string;
}

interface CardCarouselProps {
  data?: {
    title?: string;
    subtitle?: string;
    scroll?: CardProps[];
    isLive?: boolean;
    useH1Tag?: boolean;
  };
}

const HeroCollectionSlider = ({ data }: CardCarouselProps) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isAutoScroll, setIsAutoScroll] = useState(true);
  const scrollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const items = data?.scroll ?? [];
  const isLive = data?.isLive;

  const handleNext = useCallback(() => {
    setCurrentImageIndex((prev) => (prev + 1) % items.length);
  }, [items.length]);

  const handlePrev = useCallback(() => {
    setCurrentImageIndex((prev) => (prev - 1 + items.length) % items.length);
  }, [items.length]);

  useEffect(() => {
    if (!isAutoScroll || items.length <= 1) return;

    scrollIntervalRef.current = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % items.length);
    }, 6000);

    return () => {
      if (scrollIntervalRef.current) clearInterval(scrollIntervalRef.current);
    };
  }, [isAutoScroll, items.length]);

  if (!isLive || !items.length) return null;

  return (
    <section
      className="relative w-full h-[500px] md:h-[650px] lg:h-[80vh] overflow-hidden bg-neutral-100"
      aria-label="Hero Collection Slider"
    >
      {data.title && data.title.trim() !== "" && (
      data?.useH1Tag ? (
        <h1 className="sr-only">{data.title}</h1>
      ) : (
        <h2 className="sr-only">{data.title}</h2>
      ))
      }

      <div className="relative w-full h-full">
        {items.map((item, index) => {
          const isActive = index === currentImageIndex;
          const imageUrl = item.image?.data?.attributes?.url;
          const hasContent = !!item.subtitle;
          const hasUrl = !!item.mediaUrl;

          return (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${
                isActive
                  ? "opacity-100 z-10"
                  : "opacity-0 z-0 pointer-events-none"
              }`}
            >
              <div className="absolute inset-0">
                {imageUrl ? (
                  <Image
                    src={imageUrl}
                    alt={
                      item.image?.data?.attributes?.alternativeText ||
                      item.title ||
                      "Hero Slide"
                    }
                    fill
                    priority={index === 0}
                    sizes="100vw"
                    quality={80}
                    className="object-cover object-center"
                  />
                ) : (
                  <div className="w-full h-full bg-neutral-200 flex items-center justify-center text-gray-700">
                    No Image
                  </div>
                )}

                <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-black/20 to-transparent" />
              </div>

              {hasContent ? (
                <div className="relative h-full max-w-7xl mx-auto px-6 md:px-12 flex items-center">
                  <div className="max-w-2xl text-white">
                    <p className="text-sm md:text-base font-bold tracking-[0.2em] uppercase mb-4">
                      Exclusive Collection
                    </p>
                    {
                      !data?.title && index===0 ?(
                        (<h1 className="text-5xl md:text-7xl font-extrabold leading-tight mb-6">
                      {item.title}
                    </h1>)
                      ):
                    (<h2 className="text-5xl md:text-7xl font-extrabold leading-tight mb-6">
                      {item.title}
                    </h2>)
                      
                    }
                    <p className="text-lg md:text-xl text-neutral-200 mb-10">
                      {item.subtitle}
                    </p>
                    {hasUrl && (
                      <Link
                        href={item.mediaUrl || "#"}
                        className="inline-flex items-center justify-center px-10 py-4 font-bold text-white transition-all duration-300 hover:bg-neutral-900 rounded-none tracking-widest text-xs"
                        style={{ backgroundColor: "var(--secondary)" }}
                      >
                        {item.buttonText || "SHOP THE COLLECTION"}
                      </Link>
                    )}
                  </div>
                </div>
              ) : (
                hasUrl && (
                  <Link
                    href={item.mediaUrl || "#"}
                    className="absolute inset-0 z-20 cursor-pointer"
                    aria-label={item.title || "View Collection"}
                  />
                )
              )}
            </div>
          );
        })}

        {items.length > 1 && (
          <>
            <div className="absolute bottom-10 right-10 z-30 hidden md:flex space-x-4">
              <button
                onClick={() => {
                  handlePrev();
                  setIsAutoScroll(false);
                }}
                className="p-3 border border-white/30 text-white backdrop-blur-sm"
                style={{ backgroundColor: "rgba(0,0,0,0.1)" }}
                aria-label="Previous slide"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
              <button
                onClick={() => {
                  handleNext();
                  setIsAutoScroll(false);
                }}
                className="p-3 border border-white/30 text-white backdrop-blur-sm"
                style={{ backgroundColor: "rgba(0,0,0,0.1)" }}
                aria-label="Next slide"
              >
                <ChevronRight className="h-6 w-6" />
              </button>
            </div>

            <div className="absolute bottom-10 left-10 z-30 flex items-center space-x-3">
              {items.map((_, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setCurrentImageIndex(index);
                    setIsAutoScroll(false);
                  }}
                  className="py-2 focus:outline-none"
                  aria-label={`Go to slide ${index + 1}`}
                >
                  <div
                    className={`h-1 transition-all duration-300 ${
                      index === currentImageIndex
                        ? "w-12"
                        : "w-6 bg-white/40"
                    }`}
                    style={{
                      backgroundColor:
                        index === currentImageIndex
                          ? "var(--secondary)"
                          : undefined,
                    }}
                  />
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default HeroCollectionSlider;
